/**
 * Degrees Service
 * Handles all degree-related database operations
 */

import { supabase } from '@/supabase.config';
import { Degree } from '@/models/degree';
import { Database } from '@/types/supabase';
import { NotFoundError, DatabaseError } from '@/lib/errors';

// Type helpers for inserts and updates
type DegreeInsert = Database['public']['Tables']['degrees']['Insert'];
type DegreeUpdate = Database['public']['Tables']['degrees']['Update'];

/**
 * Get all active degrees
 * @throws {DatabaseError} If the database query fails
 */
export async function getAllDegrees(): Promise<Degree[]> {
  try {
    const { data, error } = await supabase
      .from('degrees')
      .select('*')
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    throw new DatabaseError('Failed to fetch degrees', error);
  }
}

/**
 * Get a degree by ID
 * @throws {NotFoundError} If degree is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getDegreeById(id: string): Promise<Degree | null> {
  try {
    const { data, error } = await supabase
      .from('degrees')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Degree with ID ${id} not found`);
      }
      throw error;
    }

    return data;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to fetch degree with ID ${id}`, error);
  }
}

/**
 * Create a new degree
 * @throws {DatabaseError} If the database operation fails
 */
export async function createDegree(degree: DegreeInsert): Promise<Degree> {
  try {
    const { data, error } = await supabase
      .from('degrees')
      .insert(degree as never)
      .select()
      .single();

    if (error) throw error;
    return data as Degree;
  } catch (error) {
    throw new DatabaseError('Failed to create degree', error);
  }
}

/**
 * Update an existing degree
 * @throws {NotFoundError} If degree is not found
 * @throws {DatabaseError} If the database operation fails
 */
export async function updateDegree(id: string, updates: DegreeUpdate): Promise<Degree> {
  try {
    const { data, error } = await supabase
      .from('degrees')
      .update({ ...updates, updated_at: new Date().toISOString() } as never)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Degree with ID ${id} not found`);
      }
      throw error;
    }

    return data as Degree;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to update degree with ID ${id}`, error);
  }
}

/**
 * Soft delete a degree (sets is_active to false)
 * @throws {DatabaseError} If the database operation fails
 */
export async function deleteDegree(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('degrees')
      .update({ is_active: false, updated_at: new Date().toISOString() } as never)
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    throw new DatabaseError(`Failed to delete degree with ID ${id}`, error);
  }
}
